<template>
  <loader :is-loading="isPending" />
  <tooltip
    ref="numbersTooltip"
    :placement="placement"
    :trigger="trigger"
    :arrow="false"
    sticky="reference"
    max-width="450"
    theme="none"
    :append-to="body"
    interactive
    :popper-options="popperOptions"
  >
    <template #default>
      <slot name="label" :numbers-tooltip-visible="numbersTooltipVisible">
        <div
          class="flex cursor-pointer justify-around bg-white p-2"
          data-cy="assign-number-toggle"
        >
          <div class="flex items-center gap-x-4">
            <icon-base :icon="IconIncomingArrow" class="rotate-180 transform" />
            <div class="flex flex-col leading-5">
              <span class="text-headline">
                {{ $t("TWILIO.COMMUNICATE_FROM") }}
              </span>
              <span v-if="!!savedPhoneNumber" class="text-label">
                {{ savedPhoneDisplay }}
              </span>
            </div>
            <icon-base :icon="IconLineArrowRight" width="12" height="12" />
          </div>
        </div>
      </slot>
    </template>
    <template #content>
      <div class="w-81 bg-white shadow-popover">
        <div class="flex flex-col gap-y-4 p-4">
          <div class="button-sequence flex w-full items-stretch">
            <div
              v-for="(value, key) in selectOptions"
              :key="key"
              data-cy="from-type"
              class="segment"
              :class="{ selected: selectedType === key }"
              @click="updateSelectedType(key)"
            >
              {{ value }}
            </div>
          </div>
          <search-input
            v-if="selectedType === PhoneSelectionType.Manual"
            ref="searchInputRef"
            v-model="search"
            no-padding
            no-margin
            @click="focusSearch"
          />
        </div>
        <div v-if="selectedType === PhoneSelectionType.Manual" class="p-2">
          <ul
            v-if="!!userAssignedPhoneNumbers?.length"
            data-cy="phones-list"
            class="flex max-h-125 flex-col space-y-1 overflow-y-auto"
          >
            <li
              v-for="phone in filteredAssignedNumbers"
              :key="phone.phone_number"
              data-cy="from-number"
              class="flex cursor-pointer items-center space-x-1 rounded-sm p-2"
              :class="
                phone.phone_number === savedPhoneNumber?.phone_number
                  ? 'bg-primary/10 text-primary hover:bg-primary/20'
                  : 'text-headline hover:bg-disabled'
              "
              @click="saveChanges(selectedType, phone.phone_number)"
            >
              <img
                src="/images/twilio.svg"
                class="h-4 cursor-pointer"
                loading="lazy"
              />
              <span>
                {{ phone.phone_number }}
                {{ phone.pilot ? `(${$t("TWILIO.PILOT")})` : "" }}
              </span>
              <icon-base
                v-if="phone.phone_number === savedPhoneNumber?.phone_number"
                :icon="IconCheck"
                class="ml-auto justify-self-end"
              />
            </li>
          </ul>
          <div v-else data-cy="empty-phone" class="text-md p-2 text-label">
            <p class="text-xs">{{ $t("TWILIO.NONE_ASSIGNED") }}</p>
          </div>
        </div>
      </div>
    </template>
  </tooltip>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { PhoneSelectionType } from "@/enums/communicationLogs";
import { PhoneNumberPreferences } from "@/enums/preferences";
import { useAuth } from "@/hooks/auth";
import { useTwilio } from "@/hooks/communicationProviders";
import { usePreferencesMutation } from "@/hooks/queries/profile/usePreferences";
import { useTwilioStore } from "@/stores/twilio";

import IconCheck from "@/components/icons/IconCheck.vue";
import IconIncomingArrow from "@/components/icons/IconIncomingArrow.vue";
import IconLineArrowRight from "@/components/icons/IconLineArrowRight.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

import type {
  PhoneNumberRoutingValue,
  UpdatePhoneNumberRoutingPayload,
} from "@/models/preferences";
import type { TippyComponent } from "vue-tippy";

const emit = defineEmits<{ updated: [] }>();

const {
  placement = "right-end",
  trigger = "mouseenter focus",
  closeOnAutoSelection = false,
} = defineProps<{
  placement?: TippyComponent["popperOptions"]["placement"];
  trigger?: TippyComponent["trigger"];
  closeOnAutoSelection?: boolean;
}>();

const { t } = useI18n();
const { currentUser } = useAuth();
const { savedPhoneNumber, savedPhoneDisplay } = useTwilio();
const twilioStore = useTwilioStore();
const { mutateAsync, isPending } = usePreferencesMutation();
const { getUserAssignedTwilioNumbers } = twilioStore;
const {
  userAssignedPhoneNumbers,
  refetchUserAssignedPhoneNumbers,
  loadingAssignedNumbers,
} = storeToRefs(twilioStore);

const body = ref(document.body);
const numbersTooltip = ref<TippyComponent | null>(null);
const popperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["left", "right"] },
    },
  ],
});
const selectOptions = ref({
  [PhoneSelectionType.Manual]: t("TWILIO.MANUAL_SELECT"),
  [PhoneSelectionType.Auto]: t("TWILIO.AUTO_SELECT"),
});
const selectedType = ref<PhoneSelectionType>(PhoneSelectionType.Manual);
const searchInputRef = ref<InstanceType<typeof SearchInput>>();
const search = ref("");

const filteredAssignedNumbers = computed(() => {
  const manualAndPilotNumbers = userAssignedPhoneNumbers.value?.filter(
    ({ manual, pilot }) => manual || pilot
  );
  if (!search.value) {
    return manualAndPilotNumbers;
  }
  return manualAndPilotNumbers?.filter((record) =>
    record.phone_number.includes(String(search.value.trim()))
  );
});

const numbersTooltipVisible = computed(
  () => numbersTooltip.value?.state?.isVisible
);

const focusSearch = () => {
  searchInputRef.value?.$el.querySelector("input")?.focus();
};

const updateSelectedType = (type: PhoneSelectionType) => {
  if (type === PhoneSelectionType.Auto) {
    saveChanges(PhoneSelectionType.Auto, null, closeOnAutoSelection);
  }
  selectedType.value = type;
};

const confirmUserHasAccessToLocallyAssignedNumber = () => {
  if (savedPhoneNumber.value?.type === PhoneSelectionType.Auto) {
    return;
  }
  const indexOfNumber = userAssignedPhoneNumbers.value?.findIndex(
    (assigned) => assigned.phone_number === savedPhoneNumber.value?.phone_number
  );
  if (indexOfNumber === -1) {
    saveChanges(PhoneSelectionType.Auto, null);
  }
};

const saveChanges = async (
  type: PhoneSelectionType,
  value: string | null,
  close = true
) => {
  const preferences: UpdatePhoneNumberRoutingPayload = {
    key: PhoneNumberPreferences.routing,
    value: {
      type,
    },
  };
  if (type === PhoneSelectionType.Manual && value) {
    (preferences.value as PhoneNumberRoutingValue)["phone_number"] = value;
  }

  await mutateAsync([preferences]);
  emit("updated");
  if (close) {
    numbersTooltip.value?.hide();
  }
};

watch(
  () => [currentUser, refetchUserAssignedPhoneNumbers.value],
  async () => {
    if (currentUser?.id && !loadingAssignedNumbers.value) {
      await getUserAssignedTwilioNumbers(currentUser.id);
      confirmUserHasAccessToLocallyAssignedNumber();
      refetchUserAssignedPhoneNumbers.value = false;
    }
  },
  {
    immediate: true,
  }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.button-sequence {
  .segment {
    @apply w-1/2 flex-none cursor-pointer border px-4 py-2-5 text-center font-semibold;
    &:first-child {
      @apply rounded-l border-r-0;
    }
    &:last-child {
      @apply rounded-r;
    }
    &.selected {
      @apply border-transparent bg-primary/10 text-primary;
    }
  }
}
</style>
